/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B090040 在庫調整入力
 */

import { Column } from "@material-table/core";
import { Box } from "@mui/material";
import {
  B000111Meisaibiko,
  B000130BumonNm,
  B000140ShainNmBumonCd,
  B000190SokoNmrSokoshubetsuKbnLocationkanriKbn,
  B000200ShohinNmr,
  B090040Create,
  B090040CreateRequest,
  B090040Get,
  B090040GetLocatoinCdByShohinCdSokoCd,
  B090040GetRequestList,
  B090040GetResponse,
  B100190Valid,
  B100190ValidRequest,
  catchRestError,
} from "clients/rest";
import {
  SDateField,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  STextField,
  SFileButton,
  SCancelButton,
  SEntryTab,
  SCodeSelect,
  msgState,
  SConfirmDialogAction,
} from "components";
import { getClientError, getClientMessage } from "messages";
import { B090040ChoseiKbn, getSCode, SCode } from "messages/codes";
import { labels } from "messages/labels";
import { B000111, B000140, B000190, B000200, B000221, B090060 } from "pages";
import { createRef, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { date, digit, mapIds } from "services";
import { locationkanriKbn, meishoKbn, sokoshubetsuKbn } from "services/codes";
import { focusState, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
// ヘッダ部
type Fields = Partial<{
  pageId: string;
  choseiYmd: string;
  shainCd: string;
  shainNm: string;
  bumonCd: string;
  bumonNm: string;
  dialogMessage: string;
}>;

// 明細部
type meiFields = Partial<{
  tab: boolean;
  shohinCd: string;
  shohinNmr: string;
  sokoCd: string;
  sokoNmr: string;
  sokoshubetsuKbn: number;
  locationkanriKbn: number;
  locationCd: string;
  choseiKbn: SCode;
  choseisu: string;
  meisaibikoCd: string;
  meisaibiko: string;
  meisaibikoCdBefore: string;
  // 倉庫種別指定用パラメータ
  sokoshubetsuList: string[];
}>;

// 明細テーブルデータ定義
type Data = Partial<{
  gyoNo: number;
  shohinCd: string;
  shohinNmr: string;
  sokoCd: string;
  sokoNmr: string;
  sokoshubetsuKbn: number;
  locationkanriKbn: number;
  locationCd: string;
  choseiKbn: SCode;
  choseiKbnNm: string;
  choseisu: string;
  meisaibikoCd: string;
  meisaibiko: string;
  meisaibikoCdBefore: string;
}>;

// フィールドの初期値を設定する。
// ヘッダ部
const defaultFields: Fields = {
  pageId: "B090040",
  choseiYmd: date(),
  shainCd: "",
  shainNm: "",
  bumonCd: "",
  bumonNm: "",
  dialogMessage: "",
};

// 明細部
const defaultMeiFields: meiFields = {
  tab: true,
  shohinCd: "",
  shohinNmr: "",
  sokoCd: "",
  sokoNmr: "",
  sokoshubetsuKbn: undefined,
  locationkanriKbn: undefined,
  locationCd: "",
  choseiKbn: null,
  choseisu: "",
  meisaibikoCd: "",
  meisaibiko: "",
  meisaibikoCdBefore: "",
  sokoshubetsuList: ["0", "3", "4", "5"],
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行　", width: "4rem", align: "right" },
  { field: "shohinCd", title: "商品コード", width: "10rem" },
  { field: "shohinNmr", title: "商品名", width: "20rem" },
  { field: "sokoCd", title: "倉庫コード", width: "10rem" },
  { field: "sokoNmr", title: "倉庫名", width: "10rem" },
  { field: "sokoshubetsuKbn", hidden: true },
  { field: "locationkanriKbn", hidden: true },
  { field: "locationCd", title: "ロケーション", width: "10rem" },
  { field: "choseiKbn", hidden: true },
  {
    field: "choseiKbnNm",
    title: "調整区分",
    width: "6rem",
    render: (data) => {
      return getSCode(B090040ChoseiKbn, data.choseiKbn?.value ?? "0")?.label;
    },
  },
  {
    field: "choseisu",
    title: "調整数　",
    width: "6rem",
    align: "right",
    render: (data) => digit(data.choseisu),
  },
  { field: "meisaibikoCd", hidden: true },
  { field: "meisaibiko", title: "明細備考", width: "20rem" },
  { field: "meisaibikoCdBefore", hidden: true },
];

const focusId = "B090040";

// B090040 在庫調整入力
export const B090040 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブル管理
  const tableRef = createRef();
  const pageSize = 15;
  const [rows, setRows] = useState<Data[]>([]);
  const [rowCount, setRowCount] = useState(0);
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  const meiMethods = useForm<meiFields>({
    mode: "onBlur",
    defaultValues: defaultMeiFields,
  });

  // フォーカス関数
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // テーブル行で選択されたデータ
  const [rowData, setRowData] = useState<Partial<Data>>();

  // ロケーションコードの活性制御(入力フィールドの必須も同時管理)
  const [disabledLocationCd, setDisabledLocationCd] = useState(true);
  // 明細削除ボタン活性制御
  const [deleteDetailButtonDisabled, setDeleteDetailButtonDisabled] =
    useState(true);
  // 登録ボタン活性制御
  const [createButtonDisabled, setCreateButtonDisabled] = useState(true);
  // 画面遷移
  const navigate = useNavigate();
  // 登録ボタン押下時の確認ダイアログオープン用
  const [openConfirm, setOpenConfirm] = useState(false);
  // チェック後の確認ダイアログオープン用
  const [openCheckedConfirm, setOpenCheckedConfirm] = useState(false);

  // 初期表示
  useEffect(() => {
    // 初期フォーカス
    focus(1);
  }, []);

  // コンポーネント
  // 担当者コード変更
  const handleChangeShainCd = () => {
    (async () => {
      // 担当者名表示処理
      await B000140ShainNmBumonCd(methods, navigate, setMsg);
      // 担当部門表示処理
      await B000130BumonNm(methods, navigate, setMsg);
    })();
  };

  // 倉庫コード変更
  const handleChangeSokoCd = () => {
    (async () => {
      // 倉庫名表示、倉庫種別区分とロケーション管理区分取得
      await B000190SokoNmrSokoshubetsuKbnLocationkanriKbn(
        meiMethods,
        navigate,
        setMsg
      );

      // 倉庫種別区分チェック
      sokoshubetsuKbnCheck();

      // ロケーションコード取得
      await B090040GetLocatoinCdByShohinCdSokoCd(meiMethods);
    })();
  };

  // 商品コード変更
  const handleChangeShohinCd = () => {
    (async () => {
      await B000200ShohinNmr(meiMethods, navigate, setMsg);

      await B090040GetLocatoinCdByShohinCdSokoCd(meiMethods);
    })();
  };

  // ロケーションコードBlur処理
  const locationCdCheck = () => {
    // フィールドの値取得
    const meiFields = meiMethods.getValues();
    // ロケーションコード存在チェック(存在しない場合何もしない)
    if (!meiFields.locationCd) {
      return;
    }
    // リクエスト作成
    const param: B100190ValidRequest = {
      sokoCd: meiFields.sokoCd!,
      locationCd: meiFields.locationCd!,
    };
    // API呼出し
    B100190Valid(param)
      .then(() => {})
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 倉庫種別区分チェック
  const sokoshubetsuKbnCheck = () => {
    // 明細入力部フィールドの値取得
    const meiFields = meiMethods.getValues();
    // 倉庫種別区分チェック
    // 1. 倉庫種別区分が0(物理倉庫)・3(材料倉庫)・4(返品倉庫)・5(その他倉庫)以外の場合、エラー
    if (
      !(
        meiFields.sokoshubetsuKbn === sokoshubetsuKbn.physicalSoko ||
        meiFields.sokoshubetsuKbn === sokoshubetsuKbn.zairyoSoko ||
        meiFields.sokoshubetsuKbn === sokoshubetsuKbn.henpinSoko ||
        meiFields.sokoshubetsuKbn === sokoshubetsuKbn.othersSoko
      )
    ) {
      setMsg(getClientError("B090040_001"));
      setDisabledLocationCd(true);
      meiMethods.setValue("locationCd", "");
      focus(7);
      return;
    }

    // 2. 倉庫種別区分が0(物理倉庫)かつロケーション管理区分が1(管理する)の場合
    if (
      meiFields.sokoshubetsuKbn === sokoshubetsuKbn.physicalSoko &&
      meiFields.locationkanriKbn === locationkanriKbn.management
    ) {
      setDisabledLocationCd(false);
      focus(6);
    } else {
      setDisabledLocationCd(true);
      meiMethods.setValue("locationCd", "");
      focus(7);
    }
  };

  // 明細テーブル行クリックイベント
  const handleClickTableRow = () => {};

  // 明細テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (rowData: Data) => {
    // タブを編集にする(同時に明細登録(更新)ボタンのラベルが「明細更新」になる)
    meiMethods.reset();
    meiMethods.setValue("tab", false);

    // 明細削除ボタンを活性化
    setDeleteDetailButtonDisabled(false);

    // ダブルクリックした行を選択状態にする
    setRowData(rowData);
    // 選択行のデータを明細入力部に設定する。
    meiMethods.setValue("choseiKbn", rowData.choseiKbn);
    meiMethods.setValue("shohinCd", rowData.shohinCd);
    meiMethods.setValue("shohinNmr", rowData.shohinNmr);
    meiMethods.setValue("sokoCd", rowData.sokoCd);
    meiMethods.setValue("sokoNmr", rowData.sokoNmr);
    meiMethods.setValue("sokoshubetsuKbn", rowData.sokoshubetsuKbn);
    meiMethods.setValue("locationkanriKbn", rowData.locationkanriKbn);
    meiMethods.setValue("locationCd", rowData.locationCd);
    meiMethods.setValue("choseisu", rowData.choseisu);
    meiMethods.setValue("meisaibikoCd", rowData.meisaibikoCd);
    meiMethods.setValue("meisaibiko", rowData.meisaibiko);
    meiMethods.setValue("meisaibikoCdBefore", rowData.meisaibikoCdBefore);

    // 倉庫種別区分チェックでロケーションコードの活性制御
    sokoshubetsuKbnCheck();
  };

  // 新規・編集タブクリックイベント
  const handleChangeMeiTab = () => {
    // 行選択解除
    setRowData(undefined);
    // 明細入力部の値を初期化(タブも初期化、明細登録(更新)ボタンのラベルを登録にする)
    meiMethods.reset();
    // 明細削除ボタン非活性化
    setDeleteDetailButtonDisabled(true);
    // ロケーションコードを入力不可にする。
    setDisabledLocationCd(true);
  };

  // 明細削除ボタンクリックイベント
  const handleClickDeteteDetailButton = () => {
    const gyoNo = rowData?.gyoNo;
    // 選択行の削除
    const newRows = rows.filter(function (source: Data) {
      return source.gyoNo !== gyoNo;
    });

    // 明細テーブルの行番号振り直し
    mapIds(newRows, "gyoNo");

    // 削除後のデータセット
    setRows(newRows);

    // 行選択の解除
    setRowData(undefined);

    // 明細入力部の初期化
    meiMethods.reset();

    // タブを新規にする
    meiMethods.setValue("tab", true);

    // 明細削除ボタン非活性化
    setDeleteDetailButtonDisabled(true);

    // ロケーションコード入力不可に
    setDisabledLocationCd(true);

    meiMethods.setValue("choseiKbn", undefined);
  };

  // 明細登録(更新)ボタンクリックイベント
  const handleClickCreateDetailButton = () => {
    // 明細入力部のフォームの値取得
    const meiFields = meiMethods.getValues();
    // 必須項目の入力チェック
    if (
      meiFields.choseiKbn?.value === "" ||
      meiFields.choseiKbn?.value === undefined
    ) {
      setMsg(getClientError("B090040_002"));
      return;
    }
    if (meiFields.shohinCd === "") {
      setMsg(getClientError("B090040_003"));
      return;
    }
    if (meiFields.sokoCd === "") {
      setMsg(getClientError("B090040_004"));
      return;
    }
    // 倉庫種別区分チェック
    // 倉庫種別区分が0(物理倉庫)・3(材料倉庫)・4(返品倉庫)・5(その他倉庫)以外の場合、エラー
    if (
      !(
        meiFields.sokoshubetsuKbn === sokoshubetsuKbn.physicalSoko ||
        meiFields.sokoshubetsuKbn === sokoshubetsuKbn.zairyoSoko ||
        meiFields.sokoshubetsuKbn === sokoshubetsuKbn.henpinSoko ||
        meiFields.sokoshubetsuKbn === sokoshubetsuKbn.othersSoko
      )
    ) {
      setMsg(getClientError("B090040_001"));
      setDisabledLocationCd(true);
      meiMethods.setValue("locationCd", "");
      return;
    }
    if (disabledLocationCd === false && meiFields.locationCd === "") {
      setMsg(getClientError("B090040_005"));
      return;
    }
    if (meiFields.choseisu === "") {
      setMsg(getClientError("B090040_006"));
      return;
    }

    // 調整数の値チェック
    if (Number(meiFields.choseisu) <= 0) {
      setMsg(getClientError("B090040_007"));
      return;
    }

    // 登録ボタン活性化
    setCreateButtonDisabled(false);

    // タブを参照し登録処理もしくは更新処理を行う。
    if (meiMethods.getValues().tab === true) {
      // 登録
      handleDetailCreate();
    } else {
      // 更新
      handleDetailUpdate();
    }

    // 明細削除ボタン非活性化
    setDeleteDetailButtonDisabled(true);
    // ロケーションコード入力不可に
    setDisabledLocationCd(true);
  };

  // 明細登録処理
  // 明細テーブルに行追加
  const handleDetailCreate = () => {
    meiMethods.handleSubmit(
      () => {
        const gyoNo: number = rows.length + 1;
        const data: Data = handleDataMapping(gyoNo);

        // 明細を設定
        setRows([...rows, data]);
        setRowCount(gyoNo);

        // 明細入力部クリア
        meiMethods.reset();

        focus(3);
      },
      (errors) => {
        console.log(errors);
        setMsg("");
      }
    )();
  };

  // 明細更新処理
  // 明細テーブルの選択行を更新
  const handleDetailUpdate = () => {
    // 明細に設定するデータ取得
    const gyoNo: number = rowData!.gyoNo!;
    const data: Data = handleDataMapping(gyoNo);

    // 明細を設定
    rows[gyoNo - 1] = data;
    setRows([...rows]);

    // 明細入力部クリア
    meiMethods.reset();
  };

  // 明細のデータマッピング
  const handleDataMapping = (gyoNo: number) => {
    // フォームのデータを取得する
    const meiFields = meiMethods.getValues();

    const data: Data = {
      gyoNo,
      shohinCd: meiFields.shohinCd,
      shohinNmr: meiFields.shohinNmr,
      sokoCd: meiFields.sokoCd,
      sokoNmr: meiFields.sokoNmr,
      sokoshubetsuKbn: meiFields.sokoshubetsuKbn,
      locationkanriKbn: meiFields.locationkanriKbn,
      locationCd: meiFields.locationCd,
      choseiKbn: meiFields.choseiKbn,
      choseiKbnNm: meiFields.choseiKbn?.label,
      choseisu: meiFields.choseisu,
      meisaibikoCd: meiFields.meisaibikoCd,
      meisaibiko: meiFields.meisaibiko,
      meisaibikoCdBefore: meiFields.meisaibikoCdBefore,
    };
    return data;
  };

  // 登録ボタン押下イベント
  const handleClickCreateButton = () => {
    // ヘッダ部のフォームの値を取得する。
    const fields = methods.getValues();

    // ヘッダ部の必須入力チェック
    if (!fields.choseiYmd) {
      setMsg(getClientError("B090040_008"));
      return;
    }
    if (!fields.shainCd) {
      setMsg(getClientError("B090040_009"));
      return;
    }

    setOpenConfirm(true);
  };

  const handleOkConfirm = () => {
    setOpenConfirm(false);
    handleCheckMei();
  };

  const handleCancelConfirm = () => {
    setOpenConfirm(false);
  };

  // 登録処理イベント
  const handleCreate = () => {
    // ヘッダ部のフォームの値を取得する。
    const fields = methods.getValues();

    // 登録パラメータを作成する。
    const param: B090040CreateRequest = {
      choseiYmd: fields.choseiYmd!,
      shainCd: fields.shainCd!,
      bumonCd: fields.bumonCd!,
      zaikoChoseiList: rows.map((value) => {
        return {
          shohinCd: value.shohinCd!,
          sokoCd: value.sokoCd!,
          choseisu: value.choseisu!,
          choseiKbn: value.choseiKbn!.value,
          locationCd: value.locationCd,
          meisaibiko: value.meisaibiko,
        };
      }),
    };
    // API呼出し
    B090040Create(param)
      .then(() => {
        methods.reset();
        meiMethods.reset();
        setRows([]);
        setMsg(getClientMessage("B090040_001"));
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 登録ボタン押下時明細チェック処理呼出しイベント
  const handleCheckMei = () => {
    // リクエスト作成
    const param: B090040GetRequestList = {
      b090040GetRequestList: rows.map((value) => {
        return {
          shohinCd: value.shohinCd!,
          sokoshubetsuKbn: value.sokoshubetsuKbn!.toString(),
          sokoCd: value.sokoCd!,
          choseisu: value.choseisu!,
          choseiKbn: value.choseiKbn!.value,
          locationCd: value.locationCd,
        };
      }),
    };

    // API呼出し
    B090040Get(param)
      .then((value) => {
        handleOpenCheckedConfirm(value.data);
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // チェック後ダイアログ制御
  const handleOpenCheckedConfirm = (data: B090040GetResponse) => {
    if (data.checkFlg === false) {
      methods.setValue("dialogMessage", data.warnMessage);
      setOpenCheckedConfirm(true);
    } else if (data.checkFlg === true) {
      handleCreate();
    }
  };

  // OK押下時
  const handleOkCheckedConfirm = () => {
    setOpenCheckedConfirm(false);
    handleCreate();
  };

  //キャンセル押下時
  const handleCancelCheckedConfirm = () => {
    setOpenCheckedConfirm(false);
  };

  // レイアウト設定
  return (
    <>
      <FormProvider {...methods}>
        <SFrame
          title="在庫調整入力"
          gridTemplateColumns="6rem 10rem 20rem 2rem 6rem 20rem "
        >
          <SLabel required>調整日</SLabel>
          <SDateField
            label="調整日"
            name="choseiYmd"
            required
            {...register(1)}
          />
          <Box gridColumn="span 3" />
          <SFileButton
            label={labels.buttons.excelImport}
            dialog={
              <B090060
                pageId={methods.watch("pageId")}
                choseiYmd={methods.watch("choseiYmd")}
                shainCd={methods.watch("shainCd")}
                bumonCd={methods.watch("bumonCd")}
              />
            }
            dialogAction="close"
            align="right"
          />

          <SLabel required>担当者</SLabel>
          <STextField
            label="担当者コード"
            name="shainCd"
            size={7}
            required
            dialog={<B000140 />}
            handleSubmit={(data: any) => {
              methods.setValue("shainCd", data.shainCd);
              methods.setValue("shainNm", data.shainNm);
              methods.setValue("bumonCd", data.bumonCd);
              methods.setValue("bumonNm", data.bumonNm);
            }}
            onBlur={handleChangeShainCd}
            {...register(2)}
          />
          <STextField label="担当者名" name="shainNm" required disabled />
          <Box />
          <SLabel>担当部門</SLabel>
          <STextField label="担当部門" name="bumonNm" disabled />
        </SFrame>
      </FormProvider>

      <SFrame>
        <STable
          keyName="gyoNo"
          data={rows}
          columns={columns}
          totalCount={rowCount}
          tableRef={tableRef}
          onRowClick={handleClickTableRow}
          handleDoubleClick={handleDoubleClickTableRow}
          fixed={3}
          width={1250}
          height={440}
          rowSelectable={meiMethods.watch("tab")}
          pageSize={pageSize}
        />
      </SFrame>

      <FormProvider {...meiMethods}>
        <SFrame>
          <SEntryTab name="tab" handleOk={handleChangeMeiTab}></SEntryTab>
          <SFrame gridTemplateColumns="5rem 3rem 10rem 24rem 5rem 10rem 10rem 10rem">
            <SLabel required gridColumn="span 2">
              調整区分
            </SLabel>
            <SLabel required>商品コード</SLabel>
            <SLabel>商品名</SLabel>
            <SLabel required gridColumn="span 2">
              倉庫
            </SLabel>
            {disabledLocationCd === true ? (
              <SLabel>ロケーション</SLabel>
            ) : (
              <SLabel required>ロケーション</SLabel>
            )}
            <SLabel required>調整数</SLabel>

            <SCodeSelect
              label="調整区分"
              name="choseiKbn"
              required
              options={B090040ChoseiKbn}
              empty
              gridColumn="span 2"
              {...register(3)}
            />
            <SNumberField
              label="商品コード"
              name="shohinCd"
              code
              size={10}
              required
              dialog={<B000200 />}
              handleSubmit={(data: any) => {
                meiMethods.setValue("shohinCd", data.shohinCd);
                meiMethods.setValue("shohinNmr", data.shohinNmr);
                B090040GetLocatoinCdByShohinCdSokoCd(meiMethods);
              }}
              onBlur={handleChangeShohinCd}
              {...register(4)}
            />
            <STextField label="商品名" name="shohinNmr" size={24} disabled />
            <SNumberField
              label="倉庫コード"
              name="sokoCd"
              code
              size={3}
              required
              dialog={
                <B000190
                  sokoshubetsuList={meiMethods.watch("sokoshubetsuList")}
                />
              }
              handleSubmit={(data: any) => {
                (async () => {
                  await meiMethods.setValue("sokoCd", data.sokoCd);
                  await meiMethods.setValue("sokoNmr", data.sokoNmr);
                  await meiMethods.setValue(
                    "sokoshubetsuKbn",
                    data.sokoshubetsuKbn
                  );
                  await meiMethods.setValue(
                    "locationkanriKbn",
                    data.locationkanriKbn
                  );
                  await sokoshubetsuKbnCheck();

                  await B090040GetLocatoinCdByShohinCdSokoCd(meiMethods);
                })();
              }}
              onBlur={handleChangeSokoCd}
              {...register(5)}
            />
            <STextField label="倉庫名" name="sokoNmr" disabled />
            {disabledLocationCd === true ? (
              <STextField
                label="ロケーション"
                name="locationCd"
                size={14}
                disabled
              />
            ) : (
              <STextField
                label="ロケーション"
                name="locationCd"
                size={14}
                location
                required
                onBlur={locationCdCheck}
                dialog={<B000221 />}
                handleSubmit={(data: any) => {
                  meiMethods.setValue("locationCd", data.locationCd);
                }}
                {...register(6)}
              />
            )}
            <SNumberField
              label="調整数"
              name="choseisu"
              size={6}
              align="right"
              required
              {...register(7)}
            />

            <SLabel gridColumn="span 4">明細備考</SLabel>
            <Box gridColumn="span 4" />
            <SNumberField
              label="明細備考コード"
              name="meisaibikoCd"
              code="true"
              size={3}
              dialog={<B000111 meishoKbn={meishoKbn.meisaibiko} />}
              handleSubmit={(data: any) => {
                meiMethods.setValue("meisaibikoCd", data.meishoCd);
                meiMethods.setValue("meisaibiko", data.meishoNm1);
                meiMethods.setValue("meisaibikoCdBefore", data.meishoCd);
              }}
              onBlur={() => B000111Meisaibiko(meiMethods, navigate, setMsg)}
              {...register(8)}
            />
            <STextField
              label="明細備考"
              name="meisaibiko"
              gridColumn="span 3"
              {...register(9)}
            />
            <Box gridColumn="span 4" />

            <SCancelButton
              label={labels.buttons.deleteDetail}
              onClick={handleClickDeteteDetailButton}
              disabled={deleteDetailButtonDisabled}
              align="left"
              {...register(10)}
            />
            <Box gridColumn="span 6" />
            {meiMethods.getValues().tab === true ? (
              <SNormalButton
                label={labels.buttons.createDetail}
                onClick={handleClickCreateDetailButton}
                align="right"
                {...register(11)}
              />
            ) : (
              <SNormalButton
                label={labels.buttons.updateDetail}
                onClick={handleClickCreateDetailButton}
                align="right"
                {...register(11)}
              />
            )}
          </SFrame>
          <SFrame>
            <SNormalButton
              label={labels.buttons.create}
              disabled={createButtonDisabled}
              onClick={handleClickCreateButton}
              align="right"
              {...register(12)}
            />
          </SFrame>
        </SFrame>
        <SConfirmDialogAction
          msg={labels.dialog.createConfirm}
          onOk={handleOkConfirm}
          onCancel={handleCancelConfirm}
          isOpen={openConfirm}
        />
        <SConfirmDialogAction
          msg={methods.watch("dialogMessage")!}
          onOk={handleOkCheckedConfirm}
          onCancel={handleCancelCheckedConfirm}
          isOpen={openCheckedConfirm}
        />
      </FormProvider>
    </>
  );
};
